import { DocEntry } from './useDocsManifest';

/**
 * Resolves the current location to the document that covers it.
 *
 * Doc frontmatter carries the same route patterns the app declares in `ROUTES`, so the
 * patterns have to be matched the way React Router would: `:param` segments stand in
 * for one path segment, a trailing `/*` covers any depth below.
 */

const escapeLiteral = (route: string) => route.replace(/[.+^${}()|[\]\\]/g, '\\$&');

export const routeToRegex = (route: string): RegExp => {
  const pattern = escapeLiteral(route)
    .replace(/\/:\w+\?/g, '(?:/[^/]+)?') // optional param: /:supportPathId?
    .replace(/:\w+/g, '[^/]+') // required param: /:id
    .replace(/\/\*$/, '(?:/.*)?'); // wildcard tail: /enablement/*
  return new RegExp(`^${pattern}/?$`);
};

/** Static segments win over parameterised ones, and longer paths over shorter. */
const specificity = (route: string): number => {
  const segments = route.split('/').filter(Boolean);
  const dynamic = segments.filter((s) => s.startsWith(':') || s === '*').length;
  return (segments.length - dynamic) * 100 + segments.length;
};

export const findDocForPath = (
  entries: DocEntry[],
  pathname: string
): DocEntry | undefined => {
  const matches = entries.filter((entry) => {
    try {
      return routeToRegex(entry.route).test(pathname);
    } catch {
      return false; // a malformed route in frontmatter must not break the drawer
    }
  });

  if (matches.length <= 1) return matches[0];
  return [...matches].sort((a, b) => specificity(b.route) - specificity(a.route))[0];
};
